import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { ArrowLeft, CheckCircle2, Clock, AlertCircle, Calendar, Flag } from 'lucide-react';
import { dates } from '../utils/dates';
type TaskStatus = 'todo' | 'in_progress' | 'done';
type TaskPriority = 'low' | 'medium' | 'high';
interface CreatorTask {
  id: string;
  title: string;
  description: string;
  status: TaskStatus;
  priority: TaskPriority;
  campaign?: string;
  deadline: string;
  createdAt: string;
}
const mockTasks: CreatorTask[] = [{
  id: '1',
  title: 'Submit Nike Instagram Post',
  description: 'Create and submit product showcase post with lifestyle photography',
  status: 'in_progress',
  priority: 'high',
  campaign: 'Nike Summer Campaign',
  deadline: dates.deliverableDeadline1,
  createdAt: dates.createdDate1
}, {
  id: '2',
  title: 'Film Nike Story Series',
  description: 'Record 3-slide behind-the-scenes story content',
  status: 'todo',
  priority: 'high',
  campaign: 'Nike Summer Campaign',
  deadline: dates.deliverableDeadline2,
  createdAt: dates.createdDate2
}, {
  id: '3',
  title: 'Review Adidas Campaign Brief',
  description: 'Read through campaign requirements and confirm deliverables',
  status: 'todo',
  priority: 'medium',
  campaign: 'Adidas Fitness Series',
  deadline: dates.taskDeadline2,
  createdAt: dates.createdDate3
}, {
  id: '4',
  title: 'Update Media Kit',
  description: 'Add latest performance metrics and recent collaborations',
  status: 'in_progress',
  priority: 'low',
  deadline: dates.taskDeadline4,
  createdAt: dates.createdDate4
}, {
  id: '5',
  title: 'Send Nike Final Deliverables',
  description: 'Package and send all final content files to brand',
  status: 'done',
  priority: 'high',
  campaign: 'Nike Summer Campaign',
  deadline: dates.taskDeadline5,
  createdAt: dates.createdDate5
}];
export function CreatorTasksPage() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState(mockTasks);
  const [filter, setFilter] = useState<'all' | TaskStatus>('all');
  const filteredTasks = filter === 'all' ? tasks : tasks.filter(t => t.status === filter);
  const stats = {
    total: tasks.length,
    todo: tasks.filter(t => t.status === 'todo').length,
    inProgress: tasks.filter(t => t.status === 'in_progress').length,
    done: tasks.filter(t => t.status === 'done').length
  };
  const handleStatusChange = (taskId: string, newStatus: TaskStatus) => {
    setTasks(prev => prev.map(t => t.id === taskId ? {
      ...t,
      status: newStatus
    } : t));
  };
  const statusConfig = {
    todo: {
      variant: 'secondary' as const,
      label: 'To Do',
      color: 'bg-gray-100 text-gray-700'
    },
    in_progress: {
      variant: 'default' as const,
      label: 'In Progress',
      color: 'bg-blue-100 text-blue-700'
    },
    done: {
      variant: 'success' as const,
      label: 'Done',
      color: 'bg-green-100 text-green-700'
    }
  };
  const priorityConfig = {
    low: {
      color: 'text-gray-500',
      bg: 'bg-gray-100'
    },
    medium: {
      color: 'text-yellow-600',
      bg: 'bg-yellow-100'
    },
    high: {
      color: 'text-red-600',
      bg: 'bg-red-100'
    }
  };
  return <div className="min-h-screen bg-gray-50 pb-12" data-id="element-920">
      <header className="bg-white border-b sticky top-0 z-10" data-id="element-921">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between" data-id="element-922">
          <div className="flex items-center" data-id="element-923">
            <Button variant="ghost" size="sm" className="mr-4" onClick={() => navigate('/creator')} data-id="element-924">
              <ArrowLeft className="h-4 w-4 mr-2" data-id="element-925" /> Back
            </Button>
            <h1 className="text-xl font-bold text-gray-900" data-id="element-926">My Tasks</h1>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-927">
          <div className="flex space-x-8 -mb-px" data-id="element-928">
            <button onClick={() => navigate('/creator')} className="pb-4 text-sm font-medium text-gray-500 hover:text-gray-700 hover:border-gray-300 border-b-2 border-transparent" data-id="element-929">
              Dashboard
            </button>
            <button onClick={() => navigate('/creator/campaigns')} className="pb-4 text-sm font-medium text-gray-500 hover:text-gray-700 hover:border-gray-300 border-b-2 border-transparent" data-id="element-930">
              Campaigns
            </button>
            <button onClick={() => navigate('/creator/deliverables')} className="pb-4 text-sm font-medium text-gray-500 hover:text-gray-700 hover:border-gray-300 border-b-2 border-transparent" data-id="element-931">
              Deliverables
            </button>
            <button className="pb-4 text-sm font-medium text-blue-600 border-b-2 border-blue-600" data-id="element-932">
              Tasks
            </button>
            <button onClick={() => navigate('/creator/brands')} className="pb-4 text-sm font-medium text-gray-500 hover:text-gray-700 hover:border-gray-300 border-b-2 border-transparent" data-id="element-933">
              Brands
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-934">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8" data-id="element-935">
          <Card data-id="element-936">
            <CardContent className="p-6" data-id="element-937">
              <div className="flex items-center justify-between" data-id="element-938">
                <div data-id="element-939">
                  <p className="text-sm text-gray-500" data-id="element-940">Total Tasks</p>
                  <p className="text-2xl font-bold text-gray-900" data-id="element-941">
                    {stats.total}
                  </p>
                </div>
                <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-942">
                  <CheckCircle2 className="h-6 w-6 text-blue-600" data-id="element-943" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-id="element-944">
            <CardContent className="p-6" data-id="element-945">
              <div className="flex items-center justify-between" data-id="element-946">
                <div data-id="element-947">
                  <p className="text-sm text-gray-500" data-id="element-948">To Do</p>
                  <p className="text-2xl font-bold text-gray-600" data-id="element-949">
                    {stats.todo}
                  </p>
                </div>
                <div className="h-12 w-12 bg-gray-100 rounded-full flex items-center justify-center" data-id="element-950">
                  <AlertCircle className="h-6 w-6 text-gray-600" data-id="element-951" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-id="element-952">
            <CardContent className="p-6" data-id="element-953">
              <div className="flex items-center justify-between" data-id="element-954">
                <div data-id="element-955">
                  <p className="text-sm text-gray-500" data-id="element-956">In Progress</p>
                  <p className="text-2xl font-bold text-blue-600" data-id="element-957">
                    {stats.inProgress}
                  </p>
                </div>
                <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-958">
                  <Clock className="h-6 w-6 text-blue-600" data-id="element-959" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-id="element-960">
            <CardContent className="p-6" data-id="element-961">
              <div className="flex items-center justify-between" data-id="element-962">
                <div data-id="element-963">
                  <p className="text-sm text-gray-500" data-id="element-964">Completed</p>
                  <p className="text-2xl font-bold text-green-600" data-id="element-965">
                    {stats.done}
                  </p>
                </div>
                <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center" data-id="element-966">
                  <CheckCircle2 className="h-6 w-6 text-green-600" data-id="element-967" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex items-center space-x-2 mb-6" data-id="element-968">
          <Button variant={filter === 'all' ? 'secondary' : 'ghost'} size="sm" onClick={() => setFilter('all')} data-id="element-969">
            All
          </Button>
          <Button variant={filter === 'todo' ? 'secondary' : 'ghost'} size="sm" onClick={() => setFilter('todo')} data-id="element-970">
            To Do
          </Button>
          <Button variant={filter === 'in_progress' ? 'secondary' : 'ghost'} size="sm" onClick={() => setFilter('in_progress')} data-id="element-971">
            In Progress
          </Button>
          <Button variant={filter === 'done' ? 'secondary' : 'ghost'} size="sm" onClick={() => setFilter('done')} data-id="element-972">
            Done
          </Button>
        </div>

        {/* Tasks List */}
        <div className="space-y-4" data-id="element-973">
          {filteredTasks.map(task => {
          const daysUntilDeadline = Math.ceil((new Date(task.deadline).getTime() - new Date().getTime()) / (1000 * 3600 * 24));
          const isOverdue = daysUntilDeadline < 0;
          const isUrgent = daysUntilDeadline >= 0 && daysUntilDeadline < 3;
          return <Card key={task.id} className="hover:shadow-md transition-shadow" data-id="element-974">
                <CardContent className="p-6" data-id="element-975">
                  <div className="flex items-start justify-between mb-3" data-id="element-976">
                    <div className="flex-1" data-id="element-977">
                      <div className="flex items-center space-x-2 mb-2" data-id="element-978">
                        <Badge variant={statusConfig[task.status].variant} className="text-xs" data-id="element-979">
                          {statusConfig[task.status].label}
                        </Badge>
                        <div className={`flex items-center space-x-1 px-2 py-0.5 rounded-full ${priorityConfig[task.priority].bg}`} data-id="element-980">
                          <Flag className={`h-3 w-3 ${priorityConfig[task.priority].color}`} data-id="element-981" />
                          <span className={`text-xs font-medium capitalize ${priorityConfig[task.priority].color}`} data-id="element-982">
                            {task.priority}
                          </span>
                        </div>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-983">
                        {task.title}
                      </h3>
                      <p className="text-sm text-gray-600 mb-2" data-id="element-984">
                        {task.description}
                      </p>
                      {task.campaign && <div className="inline-block px-2 py-1 bg-purple-50 rounded text-xs text-purple-700" data-id="element-985">
                          {task.campaign}
                        </div>}
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t" data-id="element-986">
                    <div className="flex items-center text-sm" data-id="element-987">
                      <Calendar className="h-4 w-4 mr-2 text-gray-400" data-id="element-988" />
                      <span className={isOverdue ? 'text-red-600 font-medium' : isUrgent ? 'text-orange-600 font-medium' : 'text-gray-600'} data-id="element-989">
                        {task.deadline}
                        {isOverdue && ' (Overdue)'}
                        {isUrgent && !isOverdue && ` (${daysUntilDeadline}d left)`}
                      </span>
                    </div>
                    {task.status !== 'done' && <select value={task.status} onChange={e => handleStatusChange(task.id, e.target.value as TaskStatus)} className="text-sm border border-gray-300 rounded px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-990">
                        <option value="todo" data-id="element-991">To Do</option>
                        <option value="in_progress" data-id="element-992">In Progress</option>
                        <option value="done" data-id="element-993">Done</option>
                      </select>}
                    {task.status === 'done' && <Badge variant="success" className="text-xs" data-id="element-994">
                        <CheckCircle2 className="h-3 w-3 mr-1" data-id="element-995" />
                        Completed
                      </Badge>}
                  </div>
                </CardContent>
              </Card>;
        })}
        </div>
      </main>
    </div>;
}